import React, { useEffect, useState } from 'react';
import { Bell, Heart, MessageCircle, ShoppingBag, UserPlus, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { subscribeToNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../services/notificationService';
import { Notification } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../components/common/Loader';

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const unsubscribe = subscribeToNotifications(user.id, (updatedNotifications) => {
      setNotifications(updatedNotifications);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    await markAllNotificationsAsRead(user.id);
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like': return <Heart className="text-red-500" size={20} />;
      case 'comment': return <MessageCircle className="text-blue-500" size={20} />;
      case 'follow': return <UserPlus className="text-green-500" size={20} />;
      case 'purchase': return <ShoppingBag className="text-purple-500" size={20} />;
      case 'message': return <MessageCircle className="text-primary" size={20} />;
      default: return <Bell className="text-gray-500" size={20} />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-4 pb-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Notifications {unreadCount > 0 && `(${unreadCount})`}
        </h1>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-primary text-sm font-medium flex items-center gap-1"
          >
            <Check size={16} />
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Bell size={48} className="mx-auto mb-4 opacity-50" />
          <p>No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex gap-3 ${
                !notification.read ? 'border-l-4 border-primary' : ''
              }`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                {getIcon(notification.type)}
              </div>
              
              <div className="flex-1 text-left min-w-0">
                <h3 className="font-semibold text-gray-800 mb-1">{notification.title}</h3>
                <p className="text-sm text-gray-600 mb-1">{notification.message}</p>
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                </p>
              </div>

              {notification.imageUrl && (
                <img
                  src={notification.imageUrl}
                  alt=""
                  className="w-12 h-12 object-cover rounded flex-shrink-0"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;